import { useCallback, useEffect, useState } from "react";
import type { MediaItem, Playlist } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { ClipLightbox } from "./ClipLightbox";

/**
 * Self-contained lightbox host: given an ordered `list` and the `startKey` of the
 * clicked clip, it loads that clip's collection membership, handles the multi-
 * collection toggles / new-collection creation, and walks the list on ‹ › / arrows.
 * `onChanged` fires after any membership edit so the parent can refresh its grid.
 */
export function ClipLightboxController({
  list,
  startKey,
  onClose,
  onChanged,
}: {
  list: MediaItem[];
  startKey: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [index, setIndex] = useState(() => {
    const i = list.findIndex((c) => clipKey(c) === startKey);
    return i >= 0 ? i : 0;
  });

  // Re-seek only when the parent opens a *different* clip (new startKey).
  useEffect(() => {
    const i = list.findIndex((c) => clipKey(c) === startKey);
    if (i >= 0) setIndex(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startKey]);

  // Keep the index in range if the list shrinks under us (e.g. a clip removed).
  useEffect(() => {
    if (index > list.length - 1) setIndex(Math.max(0, list.length - 1));
  }, [index, list.length]);

  const clip = list[index];

  const [collections, setCollections] = useState<Playlist[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!clip) return;
    const key = clipKey(clip);
    const [cs, mem] = await Promise.all([
      adminApi.collections
        .list()
        .then((r) => r.collections)
        .catch(() => [] as Playlist[]),
      adminApi
        .clipCollections([key])
        .then((r) => r.memberships[key] ?? [])
        .catch(() => [] as string[]),
    ]);
    setCollections(cs);
    setSelected(new Set(mem));
  }, [clip]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshCollections = useCallback(async () => {
    const cs = await adminApi.collections
      .list()
      .then((r) => r.collections)
      .catch(() => null);
    if (cs) setCollections(cs);
  }, []);

  async function toggle(id: string, isIn: boolean) {
    if (!clip) return;
    const key = clipKey(clip);
    if (isIn) await adminApi.collections.removeItem(id, key);
    else await adminApi.collections.addItem(id, clip);
    setSelected((prev) => {
      const n = new Set(prev);
      if (isIn) n.delete(id);
      else n.add(id);
      return n;
    });
    await refreshCollections();
    onChanged?.();
  }

  async function createAndAdd(name: string) {
    if (!clip) return;
    const c = await adminApi.collections.create(name);
    await adminApi.collections.addItem(c.id, clip);
    setSelected((prev) => new Set(prev).add(c.id));
    await refreshCollections();
    onChanged?.();
  }

  if (!clip) return null;

  return (
    <ClipLightbox
      clip={clip}
      collections={collections}
      selected={selected}
      onToggleCollection={toggle}
      onCreateCollection={createAndAdd}
      onPrev={index > 0 ? () => setIndex((i) => Math.max(0, i - 1)) : undefined}
      onNext={index < list.length - 1 ? () => setIndex((i) => Math.min(list.length - 1, i + 1)) : undefined}
      onBanChanged={() => onChanged?.()}
      onClose={onClose}
    />
  );
}
